import AddIcon from '@mui/icons-material/Add';
import {
  Button,
  Chip,
  Link,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router';
import { zonderLeeg } from '../../api/mutaties.ts';
import { functietitelLijst, talentpoolLijst } from '../../api/referentie.ts';
import type { Vacaturestatus } from '../../api/typen.ts';
import { useVacatures } from '../../api/vacatures.ts';
import { Laadstatus } from '../../componenten/Laadstatus.tsx';
import { Pagina } from '../../componenten/Pagina.tsx';
import { Lijstpaginering } from '../../componenten/Paginering.tsx';
import { ReferentieFilter } from '../../componenten/ReferentieFilter.tsx';
import { StatusChip } from '../../componenten/StatusChip.tsx';
import { BedrijfVerwijzing } from '../../componenten/Verwijzingen.tsx';
import { Zoekveld } from '../../componenten/Zoekveld.tsx';
import { datum, uren } from '../../helpers/formatteer.ts';
import { dienstverbandLabels, label, opties, vacaturestatusLabels } from '../../helpers/labels.ts';
import { useZoekparameters } from '../../hooks/useZoekparameters.ts';
import { VacatureFormulier } from './VacatureFormulier.tsx';

const statusOpties = opties(vacaturestatusLabels);

/**
 * Het overzicht van vacatures met zoeken en filters op status, bedrijf, talentpool en functietitel.
 * @returns De pagina.
 */
export function VacaturesPagina() {
  const { zoek, offset, limiet, krijg, zet, zetOffset } = useZoekparameters();
  const status = krijg('status') as Vacaturestatus | '';
  const bedrijfId = krijg('bedrijfId');
  const filter = zonderLeeg({
    zoek,
    status: status || undefined,
    bedrijfId,
    talentpoolId: krijg('talentpoolId'),
    functietitelId: krijg('functietitelId'),
    limiet,
    offset,
  });
  const vacatures = useVacatures(filter);
  const [nieuw, setNieuw] = useState(false);
  const navigeer = useNavigate();

  return (
    <Pagina
      titel="Vacatures"
      acties={
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNieuw(true)}>
          Nieuwe vacature
        </Button>
      }
    >
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          useFlexGap
          sx={{ flexWrap: 'wrap', alignItems: { md: 'center' } }}
        >
          <Zoekveld waarde={zoek} onChange={(waarde) => zet({ zoek: waarde })} placeholder="Zoek op titel" />
          <TextField
            select
            size="small"
            label="Status"
            value={status}
            onChange={(gebeurtenis) => zet({ status: gebeurtenis.target.value })}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">Alle statussen</MenuItem>
            {statusOpties.map((optie) => (
              <MenuItem key={optie.waarde} value={optie.waarde}>
                {optie.label}
              </MenuItem>
            ))}
          </TextField>
          <ReferentieFilter
            lijst={talentpoolLijst}
            label="Talentpool"
            waarde={krijg('talentpoolId')}
            onChange={(id) => zet({ talentpoolId: id })}
          />
          <ReferentieFilter
            lijst={functietitelLijst}
            label="Functietitel"
            waarde={krijg('functietitelId')}
            onChange={(id) => zet({ functietitelId: id })}
          />
          {bedrijfId && (
            <Chip
              label={
                <>
                  Bedrijf: <BedrijfVerwijzing id={bedrijfId} />
                </>
              }
              onDelete={() => zet({ bedrijfId: undefined })}
            />
          )}
        </Stack>
      </Paper>
      <Paper sx={{ p: 2 }}>
        <Laadstatus query={vacatures} leeg="Geen vacatures gevonden.">
          {(lijst) => (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Titel</TableCell>
                    <TableCell>Bedrijf</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Dienstverband</TableCell>
                    <TableCell>Uren</TableCell>
                    <TableCell>Startdatum</TableCell>
                    <TableCell>Gewijzigd</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lijst.map((vacature) => (
                    <TableRow key={vacature.id} hover>
                      <TableCell>
                        <Link
                          component={RouterLink}
                          to={`/vacatures/${vacature.id}`}
                          underline="hover"
                          sx={{ fontWeight: 500 }}
                        >
                          {vacature.titel}
                        </Link>
                      </TableCell>
                      <TableCell>{vacature.bedrijfId ? <BedrijfVerwijzing id={vacature.bedrijfId} /> : '–'}</TableCell>
                      <TableCell>
                        <StatusChip soort="vacature" waarde={vacature.status} />
                      </TableCell>
                      <TableCell>{label(dienstverbandLabels, vacature.dienstverband) || '–'}</TableCell>
                      <TableCell>{uren(vacature.urenMin, vacature.urenMax)}</TableCell>
                      <TableCell>{datum(vacature.startdatum)}</TableCell>
                      <TableCell>{datum(vacature.gewijzigdOp)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Laadstatus>
        <Lijstpaginering offset={offset} limiet={limiet} aantal={vacatures.data?.length ?? 0} onOffset={zetOffset} />
      </Paper>
      <VacatureFormulier
        open={nieuw}
        onSluit={() => setNieuw(false)}
        onOpgeslagen={(vacature) => void navigeer(`/vacatures/${vacature.id}`)}
      />
    </Pagina>
  );
}
